import { getApiData, listApiData, postApiData } from "@hooks/utils/api";
import { getContextualErrorMessage } from "@utils/errorHandler";
import { catchSentryError } from "@utils/sentry";
import { getPublicImageUrl } from "@utils/storage";
import { showToast } from "@utils/toast";
import * as React from "react";
import {
  decodeContractorPackage,
  decodeContractorPackageSetupLineItem,
  encodeContractorPackage,
  encodeOneTimeContractorPackage
} from "catchment-common/decoders/contractorPackage";
import {
  ContractorPackage,
  ContractorPackageSetup
} from "catchment-common/entities/contractorPackage";
import {
  ContractorPackageLineItem,
  ContractorPackageSetupLineItem
} from "catchment-common/interfaces/contractorPackage";
import { OrganisationLineItem } from "catchment-common/interfaces/organisation";
import { ProfileLineItem } from "catchment-common/interfaces/profile";

export const useContractorPackage = (
  projectId?: string,
  contractorPackageId?: string
) => {
  const [contractorPackages, setContractorPackages] = React.useState<
    ContractorPackageLineItem[]
  >([]);
  const [isContractorPackageLoading, setIsLoading] =
    React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  const handleApiError = (e: any, action: string) => {
    const message = getContextualErrorMessage(e, action);
    catchSentryError(e);
    setError(message);
  };

  const fetchData = React.useCallback(
    async (apiFunction: string, id: string, action: string) => {
      try {
        setIsLoading(true);
        const result = await getApiData(apiFunction, "contractorPackage", id);
        if (!result.data || Object.keys(result.data).length === 0) {
          return undefined;
        }
        return decodeContractorPackage(
          result.data as ContractorPackage,
          getPublicImageUrl
        );
      } catch (e: any) {
        handleApiError(e, action);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const getContractorPackage = React.useCallback(
    async (
      id: string,
      options?: { silent?: boolean }
    ): Promise<ContractorPackageLineItem | undefined> => {
      if (options?.silent) {
        try {
          const result = await getApiData(
            "getContractorPackage",
            "contractorPackage",
            id
          );
          if (!result.data || Object.keys(result.data).length === 0) {
            return undefined;
          }
          return decodeContractorPackage(
            result.data as ContractorPackage,
            getPublicImageUrl
          );
        } catch (e: any) {
          catchSentryError(e);
          return undefined;
        }
      }
      return fetchData("getContractorPackage", id, "fetch");
    },
    []
  );

  const getContractorPackageForUser = React.useCallback(
    async (id: string): Promise<ContractorPackageLineItem | undefined> => {
      return await fetchData("getContractorPackageForUser", id, "fetch");
    },
    []
  );

  const listContractorPackages = React.useCallback(
    async (projectId: string, contractorPackageId: string): Promise<void> => {
      try {
        setIsLoading(true);
        const result = await listApiData(
          "listContractorPackageDetails",
          "contractorPackage",
          projectId,
          { contractorPackageId }
        );
        const packageLineItems = await Promise.all(
          result.data.map((p) =>
            decodeContractorPackage(p as ContractorPackage, getPublicImageUrl)
          )
        );
        setContractorPackages(packageLineItems);
      } catch (e: any) {
        handleApiError(e, "list");
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const listContractorPackagesForProject = React.useCallback(
    async (
      projectId: string,
      reportDate?: string
    ): Promise<ContractorPackageLineItem[]> => {
      try {
        setIsLoading(true);
        const result = await listApiData(
          "listContractorPackages",
          "contractorPackage",
          projectId,
          { reportDate }
        );
        return Promise.all(
          result.data.map((p) =>
            decodeContractorPackage(p as ContractorPackage, getPublicImageUrl)
          )
        );
      } catch (e: any) {
        handleApiError(e, "list");
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const searchContractorPackages = React.useCallback(
    async (query: string): Promise<ContractorPackageLineItem[]> => {
      try {
        const result = await listApiData(
          "searchContractorPackages",
          "contractorPackage",
          query
        );
        return Promise.all(
          result.data.map((p) =>
            decodeContractorPackage(p as ContractorPackage, getPublicImageUrl)
          )
        );
      } catch (e: any) {
        handleApiError(e, "fetch");
        return [];
      }
    },
    []
  );

  const updateContractorPackage = React.useCallback(
    async (
      contractorPackage: ContractorPackageLineItem,
      profile?: ProfileLineItem
    ): Promise<void> => {
      try {
        setIsLoading(true);
        const encodedContractorPackageSetup = encodeOneTimeContractorPackage(
          contractorPackage,
          undefined,
          profile
        );

        await postApiData(
          "updateContractorPackage",
          "contractorPackage",
          encodedContractorPackageSetup
        );
        setContractorPackages(
          contractorPackages.map((c) =>
            c.id === contractorPackage.id ? contractorPackage : c
          )
        );
        showToast("Package Updated");
      } catch (e: any) {
        handleApiError(e, "update");
      } finally {
        setIsLoading(false);
      }
    },
    [contractorPackages]
  );

  const deleteContractorPackage = React.useCallback(
    async (contractorPackage: ContractorPackageLineItem): Promise<void> => {
      try {
        setIsLoading(true);
        const encodedContractorPackage =
          encodeContractorPackage(contractorPackage);
        await postApiData(
          "deleteContractorPackage",
          "contractorPackage",
          encodedContractorPackage
        );
        setContractorPackages(
          contractorPackages.filter((p) => p.id !== contractorPackage.id)
        );
        showToast("Package Deleted");
      } catch (e: any) {
        handleApiError(e, "delete");
      } finally {
        setIsLoading(false);
      }
    },
    [contractorPackages]
  );

  const resendInvite = React.useCallback(
    async (contractorPackageId: string) => {
      try {
        await getApiData(
          "resendInvite",
          "contractorPackage",
          contractorPackageId
        );
        showToast("Invite Resent");
      } catch (e: any) {
        handleApiError(e, "resend invite");
      }
    },
    []
  );

  const closeContractorPackage = React.useCallback(
    async (contractorPackage: ContractorPackageLineItem): Promise<void> => {
      try {
        // Optimistically update the local state immediately
        const updatedPackage = {
          ...contractorPackage,
          isClosed: true
        };
        setContractorPackages((prev) =>
          prev.map((c) => (c.id === contractorPackage.id ? updatedPackage : c))
        );

        // Use the simple update method that only changes is_closed
        await postApiData(
          "updateContractorPackageClosedStatus",
          "contractorPackage",
          {
            contractorPackageId: contractorPackage.id,
            isClosed: "true"
          } as any
        );

        showToast("Package Closed");
      } catch (e: any) {
        handleApiError(e, "close");
        // Revert optimistic update on error
        setContractorPackages((prev) =>
          prev.map((c) =>
            c.id === contractorPackage.id ? contractorPackage : c
          )
        );
      }
    },
    []
  );

  const reopenContractorPackage = React.useCallback(
    async (contractorPackage: ContractorPackageLineItem): Promise<void> => {
      try {
        // Optimistically update the local state immediately
        const updatedPackage = {
          ...contractorPackage,
          isClosed: false
        };
        setContractorPackages((prev) =>
          prev.map((c) => (c.id === contractorPackage.id ? updatedPackage : c))
        );

        // Use the simple update method that only changes is_closed
        await postApiData(
          "updateContractorPackageClosedStatus",
          "contractorPackage",
          {
            contractorPackageId: contractorPackage.id,
            isClosed: "false"
          } as any
        );

        showToast("Package Reopened");
      } catch (e: any) {
        handleApiError(e, "update");
        // Revert optimistic update on error
        setContractorPackages((prev) =>
          prev.map((c) =>
            c.id === contractorPackage.id ? contractorPackage : c
          )
        );
      }
    },
    []
  );

  const getContractorPackageSetup = async (
    id: string
  ): Promise<ContractorPackageSetupLineItem | undefined> => {
    try {
      setIsLoading(true);
      const contractorPackageResult = await getApiData(
        "getContractorPackageSetup",
        "contractorOneTimePackage",
        id
      );
      if (
        !contractorPackageResult ||
        Object.keys(contractorPackageResult.data).length === 0
      ) {
        return undefined;
      }
      return decodeContractorPackageSetupLineItem(
        contractorPackageResult.data as ContractorPackageSetup,
        getPublicImageUrl
      );
    } catch (e: any) {
      handleApiError(e, "fetch");
    } finally {
      setIsLoading(false);
    }
  };

  const createContractorPackageSetUp = async (
    organisation: OrganisationLineItem,
    contractorPackage: ContractorPackageLineItem,
    profile?: ProfileLineItem
  ): Promise<ContractorPackageSetupLineItem> => {
    try {
      setIsLoading(true);
      const encodedContractorPackage = encodeOneTimeContractorPackage(
        contractorPackage,
        organisation,
        profile
      );
      await postApiData(
        "createContractorPackage",
        "contractorPackage",
        encodedContractorPackage
      );
      setContractorPackages([...contractorPackages, contractorPackage]);
      showToast("Package Created");
      return {
        contractorPackage,
        organisation,
        profile
      };
    } catch (e: any) {
      handleApiError(e, "create");
    } finally {
      setIsLoading(false);
    }
    return {
      contractorPackage,
      organisation,
      profile
    };
  };

  React.useEffect(() => {
    if (projectId && contractorPackageId) {
      listContractorPackages(projectId, contractorPackageId);
    } else {
      setIsLoading(false);
    }
  }, [projectId, contractorPackageId]);

  return {
    closeContractorPackage,
    contractorPackages,
    createContractorPackageSetUp,
    deleteContractorPackage,
    error,
    getContractorPackage,
    getContractorPackageForUser,
    getContractorPackageSetup,
    isContractorPackageLoading,
    listContractorPackages,
    listContractorPackagesForProject,
    reopenContractorPackage,
    resendInvite,
    searchContractorPackages,
    updateContractorPackage
  };
};
